import { Component, EventEmitter, Injectable, Input, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import {
  MatTimepickerInputEvent,
  MatTimepickerIntl,
  NativeDateTimeAdapter,
  TimeAdapter
} from '@dhutaryan/ngx-mat-timepicker';

@Injectable()
export class DeliveryTimeAdapter extends NativeDateTimeAdapter {
  constructor() {
    super('ru-RU');
  }

  override parse(value: unknown): Date | null {
    if (value == null || value === '') return null;
    if (typeof value !== 'string' || !/^([01]?\d|2[0-3]):[0-5]\d$/.test(value.trim())) {
      return this.invalid();
    }
    const [hours, minutes] = value.trim().split(':').map(Number);
    // A clock value has no date or timezone conversion. The API continues to receive HH:mm.
    return new Date(2000, 0, 1, hours, minutes);
  }
}

function deliveryTimepickerIntl(): MatTimepickerIntl {
  return Object.assign(new MatTimepickerIntl(), {
    inputsTitle: 'Введите время',
    dialsTitle: 'Выберите время',
    hourInputHint: 'Часы',
    minuteInputHint: 'Минуты',
    openTimepickerLabel: 'Выбрать время',
    closeTimepickerLabel: 'Закрыть выбор времени',
    okButton: 'Готово',
    cancelButton: 'Отмена'
  });
}

@Component({
  selector: 'app-delivery-time-input',
  template: `
    <mat-form-field appearance="outline">
      <mat-label>{{ label }}</mat-label>
      <input
        matInput
        type="text"
        placeholder="ЧЧ:ММ"
        autocomplete="off"
        [formControl]="control"
        [matTimepicker]="picker"
        (input)="onTextInput($event)"
        (timeChange)="onTimeChange($event)"
      />
      <mat-timepicker-toggle
        matSuffix
        [for]="picker"
        [disabled]="control.disabled"
        [aria-label]="'Выбрать время: ' + label"
      ></mat-timepicker-toggle>
      <mat-timepicker
        #picker
        format="24h"
        mode="input"
        [disabled]="control.disabled"
      ></mat-timepicker>
    </mat-form-field>
  `,
  styles: [':host { display: inline-block; } mat-form-field { width: 100%; }'],
  providers: [
    { provide: TimeAdapter, useClass: DeliveryTimeAdapter },
    { provide: MatTimepickerIntl, useFactory: deliveryTimepickerIntl }
  ]
})
export class DeliveryTimeInputComponent {
  @Input() label = '';
  @Output() valueChange = new EventEmitter<string>();
  readonly control = new FormControl<Date | null>(null);
  private currentValue = '';

  constructor(private adapter: TimeAdapter<Date>) {}

  @Input() set value(value: string) {
    if (value === this.currentValue) return;
    this.currentValue = value || '';
    this.control.setValue(this.adapter.parse(this.currentValue, null), { emitEvent: false });
  }

  @Input() set disabled(disabled: boolean) {
    if (disabled) this.control.disable({ emitEvent: false });
    else this.control.enable({ emitEvent: false });
  }

  onTextInput(event: Event) {
    const raw = (event.target as HTMLInputElement).value;
    this.emitValue(this.adapter.parse(raw, null), raw);
  }

  onTimeChange(event: MatTimepickerInputEvent<Date>) {
    this.emitValue(event.value, (event.targetElement as HTMLInputElement).value);
  }

  private emitValue(time: Date | null, raw: string) {
    // Retain invalid text so an optional field cannot silently save as an empty time.
    this.currentValue =
      time && this.adapter.isValid(time)
        ? `${String(time.getHours()).padStart(2, '0')}:${String(time.getMinutes()).padStart(
            2,
            '0'
          )}`
        : raw.trim();
    this.valueChange.emit(this.currentValue);
  }
}
